export type BillingPermissions = {
  viewQueue: boolean;
  finalizeInvoice: boolean;
  submitApproval: boolean;
  approveInvoice: boolean;
  deliverInvoice: boolean;
  managePayment: boolean;
  manageStripe: boolean;
  correctInvoice: boolean;
  exportReport: boolean;
  clientBilling: boolean;
};

export type BillingMetrics = {
  invoiceCount: number;
  originalTotalCents: number;
  amountPaidCents: number;
  remainingBalanceCents: number;
  draftInvoices: number;
  awaitingApproval: number;
  approvedInvoices: number;
  sentInvoices: number;
  overdueInvoices: number;
  paymentFailures: number;
  paidInvoices: number;
  partialPayments: number;
  creditsCents: number;
  voidedInvoices: number;
  stripeExceptions: number;
};

export type BillingInvoiceRecord = {
  billingInvoiceId: string;
  companyId: string;
  clientId: string;
  clientName?: string;
  settlementId: string;
  settlementNumber?: string;
  invoiceDraftId: string;
  invoiceNumber: string;
  invoiceStatus: string;
  paymentStatus: string;
  stripeStatus: string;
  exceptionStatus: string;
  invoiceDate: string;
  dueDate: string;
  paymentTerms: string;
  billingContact: Record<string, unknown>;
  lineItems: Array<Record<string, unknown>>;
  originalTotalCents: number;
  amountPaidCents: number;
  remainingBalanceCents: number;
  creditCents: number;
  taxCents: number;
  currency: string;
  hostedPaymentUrl?: string | null;
  finalizedByName?: string | null;
  finalizedAt?: string | null;
  approvedByName?: string | null;
  approvedAt?: string | null;
  sentAt?: string | null;
  viewedAt?: string | null;
  paidAt?: string | null;
  version: number;
  updatedAt: string;
  createdAt: string;
};

export type BillingDetail = {
  invoice: BillingInvoiceRecord;
  versions: Array<Record<string, unknown>>;
  actions: Array<Record<string, unknown>>;
  history: Array<Record<string, unknown>>;
  payments: Array<Record<string, unknown>>;
  receipts: Array<Record<string, unknown>>;
  stripeCustomer: Record<string, unknown> | null;
  stripeInvoice: Record<string, unknown> | null;
  permissions: BillingPermissions;
};

export type BillingPolicyTemplate = {
  templateId: string;
  templateKey: string;
  templateName: string;
  description: string;
  status: string;
  triggerRules: string[];
  feeModel: Record<string, unknown>;
  paymentTerms: Record<string, unknown>;
  autopayRules: Record<string, unknown>;
  serviceHoldRules: Record<string, unknown>;
  riskRules: Record<string, unknown>;
  reminderRules: string[];
  invoiceApprovalRules: Record<string, unknown>;
  billingContacts: Record<string, unknown>;
  taxRules: Record<string, unknown>;
  currencyRules: Record<string, unknown>;
  version: number;
  updatedAt: string;
};

export type BillingClientPolicy = {
  policyId: string;
  companyId: string;
  clientId: string;
  clientName: string;
  templateId: string | null;
  templateName: string;
  policyName: string;
  status: string;
  effectiveFrom: string;
  effectiveTo: string | null;
  triggerRules: string[];
  feeModel: Record<string, unknown>;
  paymentTerms: Record<string, unknown>;
  autopayRules: Record<string, unknown>;
  serviceHoldRules: Record<string, unknown>;
  riskRules: Record<string, unknown>;
  reminderRules: string[];
  invoiceApprovalRules: Record<string, unknown>;
  billingContacts: Record<string, unknown>;
  taxRules: Record<string, unknown>;
  currencyRules: Record<string, unknown>;
  managerNotes: string;
  version: number;
  updatedAt: string;
};

export type BillingRiskProfile = {
  riskProfileId: string;
  companyId: string;
  clientId: string;
  clientName: string;
  riskLevel: string;
  paymentReliabilityScore: number;
  latePaymentCount: number;
  returnedPaymentCount: number;
  disputeCount: number;
  autopayFailureCount: number;
  serviceHoldStatus: string;
  approvalLevel: string;
  managerNotes: string;
  lastBillingReviewAt: string | null;
  version: number;
  updatedAt: string;
};

export type BillingPolicyFramework = {
  source: "canonical-ar-billing-policy-framework-d1";
  companyId: string;
  permissions: {
    viewPolicies: boolean;
    managePolicies: boolean;
    cloneTemplates: boolean;
    viewRisk: boolean;
  };
  templates: BillingPolicyTemplate[];
  policies: BillingClientPolicy[];
  riskProfiles: BillingRiskProfile[];
  evaluations: Array<{
    evaluationId: string;
    clientId: string;
    policyId: string;
    policyVersion: number;
    sourceEntityType: string;
    sourceEntityId: string;
    evaluationStatus: string;
    invoiceCreationDecision: string;
    recoveryAmountCents: number;
    calculatedFeeCents: number;
    currency: string;
    issueDate: string;
    dueDate: string;
    serviceHoldDate: string | null;
    autopayRequirement: string;
    manualReviewRequired: boolean;
    decisionReasons: unknown[];
    warnings: unknown[];
    calculationEvidence: Record<string, unknown>;
    evaluatedAt: string;
  }>;
  autopayConsents: Array<Record<string, unknown>>;
  serviceHolds: Array<Record<string, unknown>>;
  reminderSchedules: Array<Record<string, unknown>>;
  audit: Array<Record<string, unknown>>;
  clients: Array<{
    clientId: string;
    clientNumber: string;
    displayName: string;
    status: string;
    clientPortalEnabled: boolean;
    billingContactStatus: string;
  }>;
  availableRules: Record<string, string[]>;
};

export type BillingQueueFilters = {
  companyId?: string;
  search?: string;
  clientId?: string;
  invoiceStatus?: string;
  paymentStatus?: string;
  exceptionStatus?: string;
  dueStart?: string;
  dueEnd?: string;
  sort?: string;
  page?: number;
  pageSize?: number;
};

export async function listBillingInvoices(filters: BillingQueueFilters = {}) {
  return billingRequest<{
    source: "canonical-ar-billing-center-d1";
    invoices: BillingInvoiceRecord[];
    metrics: BillingMetrics;
    pagination: { page: number; pageSize: number; total: number; pageCount: number };
    permissions: BillingPermissions;
  }>(`/api/ar-recovery/billing?${params(filters)}`);
}

export async function getBillingInvoice(billingInvoiceId: string, companyId?: string) {
  return billingRequest<BillingDetail>(`/api/ar-recovery/billing/${encodeURIComponent(billingInvoiceId)}?${params({ companyId })}`);
}

export async function finalizeBillingInvoice(invoiceDraftId: string, reason: string, companyId?: string) {
  return billingRequest<{ invoice: BillingInvoiceRecord; replay: boolean }>("/api/ar-recovery/billing", {
    method: "POST",
    body: JSON.stringify({
      companyId,
      action: "finalize_invoice",
      finalize: {
        invoiceDraftId,
        reason,
        idempotencyKey: `billing-finalize:${invoiceDraftId}:${crypto.randomUUID()}`,
      },
    }),
  });
}

export async function transitionBillingInvoice(
  billingInvoiceId: string,
  input: {
    action: "submit_approval" | "return_correction" | "approve" | "finalize_delivery" | "mark_sent" | "mark_viewed" | "record_reminder" | "apply_credit" | "void" | "record_refund";
    reason: string;
    expectedVersion: number;
    creditCents?: number | null;
  },
  companyId?: string,
) {
  return billingRequest<BillingDetail>(`/api/ar-recovery/billing/${encodeURIComponent(billingInvoiceId)}/actions`, {
    method: "POST",
    body: JSON.stringify({
      companyId,
      ...input,
      idempotencyKey: `billing-action:${billingInvoiceId}:${input.action}:${input.expectedVersion}:${crypto.randomUUID()}`,
    }),
  });
}

export async function createBillingPayNowUrl(billingInvoiceId: string, companyId?: string) {
  return billingRequest<{ paymentUrl: string; providerMode: string; stripeConfiguration: Record<string, unknown>; replay: boolean }>(
    `/api/ar-recovery/billing/${encodeURIComponent(billingInvoiceId)}/pay-now`,
    {
      method: "POST",
      body: JSON.stringify({ companyId, idempotencyKey: `billing-pay-now:${billingInvoiceId}:${crypto.randomUUID()}` }),
    },
  );
}

export async function getBillingReports(companyId?: string) {
  return billingRequest<{ source: string; companyId: string; metrics: BillingMetrics; invoices: BillingInvoiceRecord[] }>(`/api/ar-recovery/billing/reports?${params({ companyId })}`);
}

export async function getBillingPolicyFramework(companyId?: string) {
  return billingRequest<BillingPolicyFramework>(`/api/ar-recovery/billing/policies?${params({ companyId })}`);
}

export async function evaluateBillingPolicy(input: {
  clientId: string;
  sourceEntityType: "VERIFIED_PAYMENT" | "SETTLEMENT" | "MANUAL_PREVIEW";
  sourceEntityId: string;
  recoveryAmountCents: number;
  contractFeePercent?: number;
  invoiceIssueDate: string;
  currentDate?: string;
  autopayEnrollmentStatus?: string;
  paymentMethodStatus?: string;
}, companyId?: string) {
  return billingRequest<Record<string, unknown>>("/api/ar-recovery/billing/policies", {
    method: "POST",
    body: JSON.stringify({
      companyId,
      action: "evaluate",
      evaluation: {
        ...input,
        idempotencyKey: `billing-policy-eval:${input.clientId}:${input.sourceEntityId}:${crypto.randomUUID()}`,
      },
    }),
  });
}

export type BillingPolicyMutationAction =
  | "create_policy"
  | "update_draft_policy"
  | "clone_policy"
  | "submit_for_approval"
  | "approve_policy"
  | "reject_policy"
  | "return_for_changes"
  | "schedule_policy"
  | "activate_policy"
  | "deactivate_policy"
  | "replace_active_policy"
  | "expire_policy"
  | "clone_template"
  | "create_policy_from_template"
  | "review_risk"
  | "apply_risk_override"
  | "remove_risk_override"
  | "approve_service_hold"
  | "cancel_service_hold"
  | "delay_service_hold"
  | "change_service_hold_scope"
  | "reinstate_service"
  | "reschedule_reminder"
  | "cancel_reminder"
  | "regenerate_reminder"
  | "add_reminder_recipient"
  | "mark_reminder_handled"
  | "save_billing_preferences"
  | "save_autopay_consent"
  | "cancel_optional_autopay"
  | "request_required_autopay_exception"
  | "save_payment_method_reference";

export async function mutateBillingPolicy(input: {
  action: BillingPolicyMutationAction;
  policyId?: string | null;
  templateId?: string | null;
  clientId?: string | null;
  reminderId?: string | null;
  serviceHoldId?: string | null;
  riskProfileId?: string | null;
  expectedVersion?: number | null;
  reason?: string | null;
  effectiveFrom?: string | null;
  effectiveTo?: string | null;
  scheduledAt?: string | null;
  replacementPolicyId?: string | null;
  status?: string | null;
  riskLevel?: string | null;
  holdScope?: string | null;
  scheduledDate?: string | null;
  channel?: string | null;
  recipient?: string | null;
  preferenceType?: string | null;
  preferenceValue?: string | null;
  consentText?: string | null;
  paymentMethodReference?: Record<string, unknown> | null;
  policy?: Partial<{
    clientId: string;
    templateId: string | null;
    policyName: string;
    triggerRules: unknown[];
    feeModel: Record<string, unknown>;
    paymentTerms: Record<string, unknown>;
    autopayRules: Record<string, unknown>;
    serviceHoldRules: Record<string, unknown>;
    riskRules: Record<string, unknown>;
    reminderRules: unknown[];
    invoiceApprovalRules: Record<string, unknown>;
    billingContacts: Record<string, unknown>;
    taxRules: Record<string, unknown>;
    currencyRules: Record<string, unknown>;
    managerNotes: string | null;
    effectiveFrom: string | null;
  }> | null;
  metadata?: Record<string, unknown> | null;
}, companyId?: string) {
  return billingRequest<Record<string, unknown>>("/api/ar-recovery/billing/policies", {
    method: "POST",
    body: JSON.stringify({
      companyId,
      action: "mutate",
      mutation: {
        ...input,
        idempotencyKey: `billing-policy:${input.action}:${crypto.randomUUID()}`,
      },
    }),
  });
}

export function billingReportCsvUrl(companyId?: string) {
  const query = params({ companyId, format: "csv" });
  return `/api/ar-recovery/billing/reports?${query}`;
}

async function billingRequest<T>(url: string, init: RequestInit = {}) {
  const response = await fetch(url, {
    credentials: "include",
    ...init,
    headers: { "Content-Type": "application/json", ...(init.headers || {}) },
  });
  if (!response.ok) throw new Error((await response.text()) || "Billing Center request failed.");
  return response.json() as Promise<T>;
}

function params(input: Record<string, string | number | null | undefined>) {
  const result = new URLSearchParams();
  Object.entries(input).forEach(([key, value]) => {
    if (value != null && value !== "") result.set(key, String(value));
  });
  return result.toString();
}
